import React, {
  useState,
  useCallback,
  useEffect,
  MouseEventHandler,
} from "react";
import styled from "styled-components";
import { Button, Input, notification } from "antd";
import { SendOutlined } from "@ant-design/icons";

import {
  Asset,
  KontaxTransactionMeta,
  TransactionSource,
  ILocalAsset,
  ITransaction,
} from "../../../../types";
import { createTransactionAsset } from "../../../common/DragAndDrop/actionHandler";
import { SpinnerBasic } from "../../../common/Spinner";
import TransactionSplitButton from "../../../common/TransactionSplitButton";
import TransactionSplitContainer from "../../../common/TransactionSplitContainer";
import { TransactionSplitState } from "../../../common/types";
import { NoteSection } from "./NoteSection";
import { InvoiceViewWrapper, SplitAndInvoiceWrapper } from "./styledComponents";
import { TransactionRow, UpdateSplitsState } from "./types";
import { EditExternalTransactionButton } from "./EditExternalTransactionButton";
import { DeleteExternalTransactionButton } from "./DeleteExternalTransactionButton";
import Upload from "../../../common/Upload";
import LocalAssetsViewer from "../../../common/LocalAssetsViewer";
import ActionLogDrawerAsync from "../../../common/ActionLogDrawerAsync";
import { isDepreciableCategoryCode } from "../../../BusinessAsset/utils";
import { AddBusinessAssetPopup } from "./AddBusinessAssetPopup";
import RecurringPaymentToggle, {
  shouldDisplayToggle,
} from "../../../common/RecurringPaymentToggle";
import { useDeleteExternalTransactionMutation } from "@/v1/api/graphql/mutations/transaction/deleteExternalTransaction";
import { showErrorMessage } from "@/v1/utils";
import {
  isInvalidRecipientVatIdForVatCategory,
  recipientVatIdForUpsert,
  showsRecipientVatIdField,
} from "../../TransactionView/helpers";
import { RECIPIENT_VAT_ID_DE_ERROR } from "@/v1/utils/recipientVatId";
import { ContactUserModal } from "../../../common/ContactUserModal";
import { ALL_TRANSACTION_REASONS } from "../../TransactionView/constants";

const ActionLogWrapper = styled.div`
  display: flex;
  flex: 1;
  justify-content: flex-end;
`;

const RecipientVatIdRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ActionReasonLabel = styled.span`
  white-space: nowrap;
`;

export const TransactionsTableExpandedRow = ({
  transaction,
  handleDeleteSplits,
  onAssetsUpdate,
  handleUpdateSplits,
  updateTransactionsMeta,
  editExternalTransactionButtonOnClick,
  onTransactionUpdated,
  onTransactionDeleted,
  onTransactionRefresh,
}: {
  transaction: TransactionRow;
  handleDeleteSplits: (
    transaction: TransactionRow,
    state: TransactionSplitState
  ) => Promise<boolean>;
  onAssetsUpdate: (transactionId: string, assets: Array<Asset>) => void;
  handleUpdateSplits: ({
    transaction,
    splitState,
    skipConfirmationPopup,
  }: UpdateSplitsState) => Promise<void>;
  updateTransactionsMeta: (
    payload: Array<KontaxTransactionMeta>
  ) => Promise<boolean>;
  editExternalTransactionButtonOnClick: MouseEventHandler<HTMLButtonElement>;
  onTransactionUpdated: (upsertedTransaction: ITransaction) => void;
  onTransactionDeleted: (transactionId: string) => void;
  onTransactionRefresh?: (transactionId: string) => Promise<void>;
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [showContactUserModal, setShowContactUserModal] = useState(false);
  const [isSplitMode, setIsSplitMode] = useState(!!transaction.splits?.length);
  const [showAddBusinessAssetModal, setShowAddBusinessAssetModal] =
    useState<boolean>(false);
  const [recipientVatIdInput, setRecipientVatIdInput] = useState(
    () => transaction.recipientVatId ?? ""
  );
  const [isSavingRecipientVatId, setIsSavingRecipientVatId] = useState(false);

  useEffect(() => {
    setRecipientVatIdInput(transaction.recipientVatId ?? "");
  }, [transaction.id, transaction.recipientVatId, transaction.vatCategoryCode]);

  const showSplitButton = !transaction.splits?.length && !isSplitMode;

  const customerName =
    [transaction.firstName, transaction.lastName].filter(Boolean).join(" ") ||
    "den Kunden";

  // Fall back to the raw enum value so a reason we have no label for is still
  // visible rather than silently dropped.
  const actionReasonLabel = transaction.actionReason
    ? ALL_TRANSACTION_REASONS.find(
        ({ value }) => value === transaction.actionReason
      )?.label ?? transaction.actionReason
    : null;

  const handleSaveRecipientVatId = useCallback(async () => {
    if (
      isInvalidRecipientVatIdForVatCategory(
        transaction.vatCategoryCode,
        recipientVatIdInput
      )
    ) {
      notification.error({ message: RECIPIENT_VAT_ID_DE_ERROR });
      return;
    }

    setIsSavingRecipientVatId(true);
    try {
      const ok = await updateTransactionsMeta([
        {
          kontistTransactionId: transaction.id,
          recipientVatId: recipientVatIdForUpsert(
            transaction.vatCategoryCode,
            recipientVatIdInput
          ),
        },
      ]);
      if (ok) {
        notification.success({ message: "Recipient VAT ID saved" });
      }
    } finally {
      setIsSavingRecipientVatId(false);
    }
  }, [
    transaction.id,
    transaction.vatCategoryCode,
    recipientVatIdInput,
    updateTransactionsMeta,
  ]);

  const renderInvoiceContent = useCallback(
    (transaction: TransactionRow) => {
      if (transaction.assets?.length) {
        return (
          <LocalAssetsViewer
            topPagination
            useReceiptRemoval
            stickyReceiptRemoval
            assets={transaction.assets || []}
            id={`asset-${transaction.id}`}
            minHeight={1000}
            onDelete={(asset: Asset | ILocalAsset) => {
              const assets =
                transaction.assets?.filter(
                  (transactionAsset) => transactionAsset !== asset
                ) || [];
              onAssetsUpdate(transaction.id, assets);
            }}
          />
        );
      } else {
        return <p style={{ textAlign: "center" }}> No Invoice found</p>;
      }
    },
    [onAssetsUpdate]
  );

  const renderInvoiceComponent = useCallback(
    (transaction: TransactionRow) => {
      return (
        <InvoiceViewWrapper>
          <Upload
            onDropFiles={async (files: File[]) => {
              try {
                setIsUploading(true);

                const assets = (
                  await Promise.all(
                    files.map(async (file: File) => ({
                      id: await createTransactionAsset(transaction.id, file),
                      fullsize: URL.createObjectURL(file),
                      filetype: file.type.split("/").pop() as string,
                    }))
                  )
                ).concat(transaction.assets || ([] as any));

                onAssetsUpdate(transaction.id, assets);
              } catch (error) {
              } finally {
                setIsUploading(false);
              }
            }}
          >
            {renderInvoiceContent(transaction)}
            {isUploading ? (
              <SpinnerBasic />
            ) : (
              <p>Drag and drop invoices here to upload</p>
            )}
          </Upload>
        </InvoiceViewWrapper>
      );
    },
    [renderInvoiceContent, onAssetsUpdate, isUploading]
  );

  const showEditButton =
    [TransactionSource.BACKOFFICE_MANUAL, TransactionSource.USER].includes(
      transaction.source as TransactionSource
    ) && !transaction.splits?.length;

  const [deleteExternalTransaction] = useDeleteExternalTransactionMutation({
    onCompleted: ({ deleteExternalTransaction }) => {
      if (deleteExternalTransaction.success) {
        onTransactionDeleted(transaction.id);
      } else {
        showErrorMessage("Failed to delete transaction");
      }
    },
    onError: (error) => {
      showErrorMessage(`Failed to delete transaction: ${error.message}`);
    },
  });

  // this button is only visible on non-manual, outgoing, unverified, non 1320 / 1370 categories transactions
  const shouldShowAddBusinessAssetButton =
    !transaction.splits?.length &&
    (transaction.amount || 0) < 0 &&
    !transaction.verified &&
    !isDepreciableCategoryCode(transaction.categoryCode) &&
    transaction.source !== TransactionSource.BACKOFFICE_MANUAL;

  return (
    <div>
      <div
        style={{
          display: "flex",
          gap: "24px",
          padding: "24px",
          alignItems: "center",
        }}
      >
        {showSplitButton && (
          <TransactionSplitButton onClick={() => setIsSplitMode(true)} />
        )}
        <NoteSection
          initialValue={transaction.internalNote || ""}
          onSave={async (note?: string) => {
            return updateTransactionsMeta([
              {
                kontistTransactionId: transaction.id,
                internalNote: note,
              },
            ]);
          }}
          personalNote={transaction.personalNote}
        />
        <Button
          size="large"
          data-testid="contact-user-button"
          onClick={() => setShowContactUserModal(true)}
        >
          <SendOutlined /> Contact user
        </Button>
        {actionReasonLabel && (
          <ActionReasonLabel data-testid="action-reason-label">
            Action Reason: <b>{actionReasonLabel}</b>
          </ActionReasonLabel>
        )}
        {/* removed from the tree while closed so the reason picker starts fresh */}
        {showContactUserModal && (
          <ContactUserModal
            show
            onHide={() => setShowContactUserModal(false)}
            name={customerName}
            amount={transaction.amount}
            transactionId={transaction.id}
            onAfterSend={() => onTransactionRefresh?.(transaction.id)}
          />
        )}
        {showsRecipientVatIdField(transaction.vatCategoryCode) && (
          <RecipientVatIdRow data-testid="recipient-vat-id-row">
            <span style={{ whiteSpace: "nowrap" }}>VAT ID</span>
            <Input
              value={recipientVatIdInput}
              onChange={(e) => setRecipientVatIdInput(e.target.value)}
              placeholder="Recipient VAT ID"
              status={
                isInvalidRecipientVatIdForVatCategory(
                  transaction.vatCategoryCode,
                  recipientVatIdInput
                )
                  ? "error"
                  : undefined
              }
              style={{ width: 150 }}
              maxLength={20}
              data-testid="recipient-vat-id-input"
            />
            <Button
              type="primary"
              loading={isSavingRecipientVatId}
              onClick={handleSaveRecipientVatId}
              data-testid="recipient-vat-id-save"
            >
              Save
            </Button>
          </RecipientVatIdRow>
        )}
        {shouldDisplayToggle(transaction) && (
          <RecurringPaymentToggle
            transaction={transaction}
            recurringPaymentRuleId={transaction.recurringPaymentRuleId}
          />
        )}
        {!!transaction.recurringPaymentRuleId && (
          <Button
            type="link"
            style={{ padding: 0 }}
            onClick={() =>
              window
                .open(`/rules-view/${transaction.recurringPaymentRuleId}`)
                ?.focus()
            }
          >
            Open rule
          </Button>
        )}
        {showEditButton && (
          <EditExternalTransactionButton
            onClick={editExternalTransactionButtonOnClick}
            isDisabled={
              transaction.verified ||
              isDepreciableCategoryCode(transaction.categoryCode)
            }
          />
        )}
        {transaction.source &&
          [
            TransactionSource.USER,
            TransactionSource.BACKOFFICE_MANUAL,
            TransactionSource.CSV_IMPORT,
          ].includes(transaction.source) &&
          !transaction.verified && (
            <DeleteExternalTransactionButton
              onDelete={() =>
                deleteExternalTransaction({ variables: { id: transaction.id } })
              }
            />
          )}
        {shouldShowAddBusinessAssetButton && (
          <Button
            size="large"
            disabled={!transaction.assets?.length}
            onClick={() => setShowAddBusinessAssetModal(true)}
          >
            Add business asset
          </Button>
        )}
        {/* should remove the component completely to reset the state */}
        {showAddBusinessAssetModal && (
          <AddBusinessAssetPopup
            initialValues={{
              id: transaction.id,
              categoryCode: transaction.categoryCode,
              vatCategoryCode: transaction.vatCategoryCode,
              amount: transaction.amount,
              paymentDate: transaction.paymentDate,
            }}
            closePopup={() => setShowAddBusinessAssetModal(false)}
            onTransactionUpdated={onTransactionUpdated}
            vatYearPaymentFrequency={transaction.vatYearPaymentFrequency}
          />
        )}
        <ActionLogWrapper>
          <ActionLogDrawerAsync
            title="Action log"
            modelName="transaction"
            recordIds={[transaction.id]}
            small
          />
        </ActionLogWrapper>
      </div>
      <SplitAndInvoiceWrapper>
        {renderInvoiceComponent(transaction)}
        {isSplitMode && (
          <TransactionSplitContainer
            transaction={transaction}
            onSubmit={(splitState: TransactionSplitState) =>
              handleUpdateSplits({
                transaction,
                splitState,
              })
            }
            onDelete={async (state: TransactionSplitState) => {
              if (await handleDeleteSplits(transaction, state)) {
                setIsSplitMode(false);
                return true;
              }
              return false;
            }}
            disableBusinessAssetFeatures
          />
        )}
      </SplitAndInvoiceWrapper>
    </div>
  );
};
